import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslocoDirective } from '@jsverse/transloco';

import {
	PRISM_DIALOG_DATA,
	PrismButtonComponent,
	PrismDialogComponent,
	PrismDialogRef,
	PrismIconComponent,
	PrismListComponent,
	PrismListItemComponent,
	PrismProgressIndicatorComponent,
	PrismSearchBarComponent
} from '@shure/prism-angular-components';
import { provideInlineScope } from '@shure/shared/angular/utils/i18n';

import { OrganizationItem, VerificationStatus } from '../../models/organization-item.model';
import { OrganizationApi } from '../../models/switch-organization-api';

export interface SwitchOrganizationDialogData {
	showCreateButton?: boolean;
}

@Component({
	selector: 'sh-switch-organization-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './switch-organization-dialog.component.html',
	styleUrl: './switch-organization-dialog.component.scss',
	imports: [
		TranslocoDirective,
		PrismButtonComponent,
		PrismDialogComponent,
		PrismIconComponent,
		PrismListComponent,
		PrismListItemComponent,
		PrismProgressIndicatorComponent,
		PrismSearchBarComponent
	],
	providers: [provideInlineScope('scSwitchOrganization', (lang) => import(`../../../i18n/${lang}`))]
})
export class SwitchOrganizationDialogComponent {
	private readonly api = inject(OrganizationApi);
	private readonly dialogRef = inject(PrismDialogRef, { optional: true });
	private readonly dialogData = inject<SwitchOrganizationDialogData>(PRISM_DIALOG_DATA, { optional: true });

	public readonly showCreateButton = input(this.dialogData?.showCreateButton ?? false);
	public readonly organizationSelected = output<string>();
	public readonly createRequested = output<void>();
	public readonly closed = output<void>();

	public readonly searchQuery = signal('');
	public readonly selectedOrg = signal<OrganizationItem | null>(null);

	public readonly verificationStatus = VerificationStatus;

	protected readonly currentOrgId = rxResource({ loader: () => this.api.getCurrentOrgId$() });
	protected readonly organizations = rxResource({ loader: () => this.api.getOrganizations() });

	protected readonly loading = computed(() => this.currentOrgId.isLoading() || this.organizations.isLoading());

	protected readonly allOrganizations = computed(() => {
		const currentOrgId = this.currentOrgId.value() ?? '';
		const organizations = this.organizations.value() ?? [];

		return this.moveCurrentOrgToTop(organizations, currentOrgId);
	});

	public readonly filteredOrganizations = computed(() => {
		const query = this.searchQuery().trim().toLowerCase();
		const orgs = this.allOrganizations();
		if (!query) {
			return orgs;
		}
		return orgs.filter(
			(org) => org.name.toLowerCase().includes(query) || org.companyName.toLowerCase().includes(query)
		);
	});

	public applyFilter(searchKey: string): void {
		this.searchQuery.set(searchKey);
	}

	public selectOrganization(org: OrganizationItem): void {
		if (org.id !== this.currentOrgId.value()) {
			this.selectedOrg.set(org);
		}
	}

	public confirmSwitch(): void {
		const selected = this.selectedOrg();
		if (selected) {
			this.api.switchOrganization(selected.id);
			this.organizationSelected.emit(selected.id);
		}
	}

	public closeDialog(): void {
		this.dialogRef?.close();
		this.closed.emit();
	}

	public openCreateOrganization(): void {
		this.createRequested.emit();
	}

	private moveCurrentOrgToTop(orgs: OrganizationItem[], currentId: string): OrganizationItem[] {
		const idx = orgs.findIndex((o) => o.id === currentId);
		if (idx <= 0) {
			return orgs;
		}
		const result = [...orgs];
		const [current] = result.splice(idx, 1);
		result.unshift(current);
		return result;
	}
}
